import React from 'react';

// ── Circular SVG progress ring ────────────────────────────────────────────────
function CircularProgressRing({ percentage = 0, size = 64 }) {
    const pct = Math.min(100, Math.max(0, percentage));
    const radius = (size - 8) / 2;          // 4px stroke on each side
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - pct / 100);

    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-label={`${pct}% complete`}>
            {/* Track */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth={6}
            />
            {/* Progress arc */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#FFFFFF"
                strokeWidth={6}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 600ms ease' }}
            />
            {/* Percentage label — counter-rotate so text reads upright */}
            <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="central"
                fill="white"
                fontWeight="bold"
                fontSize={size / 4}
                style={{ transform: `rotate(90deg)`, transformOrigin: 'center', fontFamily: 'inherit' }}
            >
                {pct}%
            </text>
        </svg>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SprintProgressReport({
    completionRate = 0,
    carryForwardCount = 0,
    sprintName = null,
    onGenerateReport,
}) {
    const summaryText = completionRate >= 90
        ? `${sprintName || 'Sprint'} is on track for high-efficiency completion.`
        : completionRate >= 60
            ? `${sprintName || 'Sprint'} is progressing — some tasks need attention.`
            : `${sprintName || 'Sprint'} requires immediate focus to meet completion targets.`;

    return (
        <section className="bg-[#003263] text-white rounded-2xl p-6 shadow-lg flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Left — ring + summary */}
            <div className="flex items-center gap-4">
                <CircularProgressRing percentage={completionRate} size={72} />
                <div>
                    <h3 className="text-lg font-black font-headline">AI Sprint Summary</h3>
                    <p className="text-blue-100 text-sm max-w-xs">{summaryText}</p>
                </div>
            </div>

            {/* Center — metrics */}
            <div className="flex gap-8">
                <div className="text-center">
                    <p className="text-xs font-bold uppercase tracking-widest text-blue-200">Completion Rate</p>
                    <p className="text-2xl font-black font-headline">{completionRate}%</p>
                </div>
                <div className="text-center">
                    <p className="text-xs font-bold uppercase tracking-widest text-blue-200">Carry Forward</p>
                    <p className="text-2xl font-black font-headline">
                        {String(carryForwardCount).padStart(2, '0')}
                    </p>
                </div>
            </div>

            {/* Right — action */}
            <button
                onClick={onGenerateReport}
                className="bg-white text-[#003263] px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all flex-shrink-0"
            >
                Generate Report
            </button>
        </section>
    );
}
